<script setup>
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  width: {
    type: String,
    default: '640px',
  },
})

defineEmits(['close'])

const { t } = useI18n()
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed flex justify-end bg-white/70"
        style="z-index: 150; top: 0; left: 0; right: 0; bottom: 0;"
        @click.self="$emit('close')"
      >
        <div
          class="bg-white h-full flex flex-col shadow-2xl border-l border-dss-surface-200"
          :style="{
            width: props.width,
            maxWidth: '100%',
          }"
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-dss-surface-200">
            <h2 class="font-hellix text-xl font-[450] leading-none">
              {{ t('newBradley.aiPolicyModal.title') }}
            </h2>
            <button
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              @click="$emit('close')"
            >
              <i class="pi pi-times text-gray-600 text-lg" />
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 py-6 space-y-6 text-dss-primary-950">
            <h1 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-4">
              {{ t('newBradley.aiPolicyModal.mainTitle') }}
            </h1>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.purposeTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.purposeBody') }}
              </p>
            </div>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.scopeTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.scopeAppliesTo') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.scopeItem1') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.scopeItem2') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.scopeItem3') }}
              </p>
            </div>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.guidingPrinciplesTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.guidingPrinciplesIntro') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle1') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle2') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle3') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle4') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle5') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.principle6') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.principle7') }}
              </p>
            </div>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.rolesTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.rolesIntro') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem1') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem2') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem3') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem4') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem5') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem6') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.rolesItem7') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.rolesItem8') }}
              </p>
            </div>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.governanceTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.governanceP1') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.governanceP2') }}
              </p>

              <div class="overflow-x-auto mb-4">
                <table class="min-w-full border border-dss-surface-200">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="border border-dss-surface-200 px-4 py-2 text-left text-sm font-semibold">
                        {{ t('newBradley.aiPolicyModal.tableRole') }}
                      </th>
                      <th class="border border-dss-surface-200 px-4 py-2 text-left text-sm font-semibold">
                        {{ t('newBradley.aiPolicyModal.tableResponsibilities') }}
                      </th>
                      <th class="border border-dss-surface-200 px-4 py-2 text-left text-sm font-semibold">
                        {{ t('newBradley.aiPolicyModal.tableDeliverables') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow1Role') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow1Resp') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow1Deliver') }}
                      </td>
                    </tr>
                    <tr>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow2Role') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow2Resp') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow2Deliver') }}
                      </td>
                    </tr>
                    <tr>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow3Role') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow3Resp') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow3Deliver') }}
                      </td>
                    </tr>
                    <tr>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow4Role') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow4Resp') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow4Deliver') }}
                      </td>
                    </tr>
                    <tr>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow5Role') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow5Resp') }}
                      </td>
                      <td class="border border-dss-surface-200 px-4 py-2 text-sm">
                        {{ t('newBradley.aiPolicyModal.govRow5Deliver') }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-4">
                {{ t('newBradley.aiPolicyModal.governanceP3') }}
              </p>
            </div>

            <div>
              <h2 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-3">
                {{ t('newBradley.aiPolicyModal.monitoringTitle') }}
              </h2>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.monitoringP1') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.monitoringP2') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.monitoringP3') }}
              </p>
              <p class="font-inter text-sm font-normal leading-5 tracking-[0.005em] mb-2">
                {{ t('newBradley.aiPolicyModal.monitoringP4') }}
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-dss-surface-200">
            <button
              class="w-full py-3 bg-dss-primary-500 hover:bg-dss-primary-600 text-white font-medium rounded-lg transition-colors font-hellix"
              @click="$emit('close')"
            >
              {{ t('newBradley.aiPolicyModal.next') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
